'use client';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

import BellIcon from '../../icons/Bell';
import idsTokensDefault from '../../utils/tokens/idsTokens';
import Badge from '../Badge';
import ListItem from '../ListItem';
import ListSubheader from '../ListSubheader';
import NavigationBarButton from '../NavigationBar/NavigationBarButton';
import PopperMenu from '../PopperMenu';
import Typography from '../Typography';
import NotificationsItem from './NotificationsItem';
import NotificationsSubMenu from './NotificationsSubMenu';
import useMenu from './useMenu';

export const styles = ({ idsTokens = idsTokensDefault }, { variant = 'classic' }) => ({
  paper: {
    width: 400,
  },
  menuList: {
    paddingTop: idsTokens.menu.default.spacing.padding.list.top,
    paddingBottom: idsTokens.menu.default.spacing.padding.list.bottom,
    maxHeight: 400,
    overflowY: 'auto',
    '& .MuiMenuItem-root, & .MuiListSubheader-root': {
      backgroundColor: 'transparent',
    },
  },
  icon: {
    width: idsTokens.navbar[variant].sizing.icon.width,
    height: idsTokens.navbar[variant].sizing.icon.height,
    color: idsTokens.navbar[variant].color.content.item.enabled,
  },
  headerContainer: {
    paddingTop: idsTokens.navbar[variant].spacing.menuItem.top,
    paddingBottom: idsTokens.navbar[variant].spacing.menuItem.bottom,
    /**
     * Figma shows the header being shift to the right with an invisible unread
     * marker Icon, we do not have that here so I add 32px to the left padding
     * that represents 20px from the icon, 8x from its margin and 4px from
     * the ListItem gap
     */
    paddingLeft: `calc(${idsTokens.navbar[variant].spacing.menuItem.left} + 32px)`,
    paddingRight: idsTokens.navbar[variant].spacing.menuItem.right,
    gap: idsTokens.navbar[variant].spacing.menuItem.gap,
  },
  emptyNotificationsMenu: {
    padding: '32px 16px',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    // minHeight: 260,
    flexDirection: 'column',
  },
  emptyIcon: {
    color: idsTokens.navbar[variant].color.notificationBell,
    fontSize: 160,
  },
});

const useStyles = makeStyles()(styles);

export const NotificationsMenu = ({
  notifications = [],
  notificationIdField = 'id',
  onToggleRead,
  onDelete,
  onClick,
  onButtonClick,
  headerText = 'Notifications',
  emptyText = 'All squeaky clean',
  removeText = 'Remove',
  markReadText = 'Mark as read',
  markUnreadText = 'Mark as unread',
  actionsMenuItems,
  variant,
  ...rest
}) => {
  const { classes, theme } = useStyles({ variant });
  const idsTokens = theme?.idsTokens ?? idsTokensDefault;
  const [open, anchorRef, openMenu, closeMenu] = useMenu(false);

  const newNotificationCount = notifications.reduce(
    (acc, { unread }) => (unread ? acc + 1 : acc),
    0
  );

  return (
    <>
      <NavigationBarButton
        open={open}
        icon={
          <Badge
            badgeContent={newNotificationCount}
            borderColor="defaultInverse"
            max={999}
            sx={{ '.MuiBadge-badge': { right: '-15px' } }}
          >
            <BellIcon fontSize="small" />
          </Badge>
        }
        handleClick={openMenu}
        onClick={onButtonClick}
        ref={anchorRef}
        variant={variant}
        {...rest}
      />
      <PopperMenu
        open={open}
        onClose={closeMenu}
        anchorEl={anchorRef.current}
        classes={{
          paper: classes.paper,
        }}
        placement="bottom-end"
        MenuListProps={{
          classes: {
            root: classes.menuList,
          },
        }}
        popperOptions={{ strategy: 'fixed' }}
        modifiers={[
          {
            name: 'offset',
            options: { offset: [0, 8] },
          },
        ]}
      >
        {notifications.length > 0 && (
          <ListSubheader disableSticky className={classes.headerContainer}>
            <Typography
              variant="bodyDefault"
              emphasis="high"
              sx={{
                '&': {
                  color: idsTokens.listItem.default.color.text.label.enabled,
                },
              }}
            >
              {headerText}
            </Typography>
            {actionsMenuItems && <NotificationsSubMenu actionsMenuItems={actionsMenuItems} />}
          </ListSubheader>
        )}
        {notifications.map((notification, i) => (
          <NotificationsItem
            key={notification[notificationIdField] || notification.key || 'item' + i}
            id={notification[notificationIdField]}
            handleClose={closeMenu}
            onToggleRead={onToggleRead}
            onDelete={onDelete}
            onClick={onClick}
            removeText={removeText}
            markReadText={markReadText}
            markUnreadText={markUnreadText}
            hasDivider={i !== 0}
            variant={variant}
            {...notification}
          />
        ))}
        {notifications.length === 0 && (
          <ListItem className={classes.emptyNotificationsMenu}>
            <BellIcon className={classes.emptyIcon} />
            {emptyText}
          </ListItem>
        )}
      </PopperMenu>
    </>
  );
};

export default NotificationsMenu;
